import { type JSX, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { Spinner } from '@heroui/react';
import clsx from 'clsx';

import { usePatternManifest } from '~/hooks/useDesignPatterns';
import { useRefArchManifest } from '~/hooks/useReferenceArchitectures';
import { usePatternsStore } from '~/stores/patternsStore';

import { PatternCard } from './components/PatternCard';
import { PatternFilters } from './components/PatternFilters';
import { filterPatterns, formatDomainLabel, groupByDomain } from './utils';

export function PatternsHome(): JSX.Element {
  const { error, isLoading, manifest } = usePatternManifest();
  const { manifest: refArchManifest } = useRefArchManifest();
  const { searchQuery, selectedDomains, selectedRefArchId, selectedSeverities } =
    usePatternsStore();
  const [searchParams, setSearchParams] = useSearchParams();

  // Read initial domain to expand from URL (e.g., ?domain=frontend)
  const initialDomain = searchParams.get('domain');

  // All domains start collapsed, unless a domain was specified in the URL
  const [expandedDomains, setExpandedDomains] = useState<Set<string>>(
    initialDomain ? new Set([initialDomain]) : new Set(),
  );

  // Clear URL params after applying them (once on mount, StrictMode-safe)
  const paramsCleared = useRef(false);
  useEffect(() => {
    if (!paramsCleared.current && searchParams.has('domain')) {
      paramsCleared.current = true;
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const toggleExpanded = useCallback((domain: string) => {
    setExpandedDomains(prev => {
      const next = new Set(prev);
      if (next.has(domain)) next.delete(domain);
      else next.add(domain);
      return next;
    });
  }, []);

  const allPatterns = useMemo(() => manifest?.patterns ?? [], [manifest]);

  // Compute the set of pattern_ids that belong to the selected ref arch
  const refArchPatternIds = useMemo(() => {
    if (!selectedRefArchId || !refArchManifest) return null;
    const entry = refArchManifest.reference_architectures.find(
      ra => ra.ref_arch_id === selectedRefArchId,
    );
    return entry ? new Set(entry.pattern_ids) : null;
  }, [selectedRefArchId, refArchManifest]);

  const filtered = useMemo(
    () =>
      filterPatterns(allPatterns, {
        searchQuery,
        selectedDomains,
        selectedSeverities,
        refArchPatternIds,
      }),
    [allPatterns, searchQuery, selectedDomains, selectedSeverities, refArchPatternIds],
  );

  const grouped = useMemo(() => groupByDomain(filtered), [filtered]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-20 text-center text-error">
        Failed to load design patterns. Please try again later.
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground">Design Patterns</h1>
        <p className="text-sm text-muted mt-1">
          {allPatterns.length} patterns across {new Set(allPatterns.map(p => p.domain)).size}{' '}
          domains
        </p>
      </div>

      <PatternFilters patterns={allPatterns} filteredCount={filtered.length} />

      {filtered.length === 0 ? (
        <div className="py-12 text-center text-muted">
          No patterns match your filters. Try adjusting your search or clearing filters.
        </div>
      ) : (
        <div className="space-y-3">
          {Array.from(grouped.entries()).map(([domain, patterns]) => {
            const isOpen = expandedDomains.has(domain);

            return (
              <section key={domain}>
                <button
                  type="button"
                  onClick={() => toggleExpanded(domain)}
                  className="flex items-center gap-2 w-full px-4 py-3 rounded-lg bg-surface-elevated hover:bg-surface-elevated/80 transition-colors cursor-pointer"
                >
                  <ChevronDownIcon
                    className={clsx('h-4 w-4 text-muted transition-transform flex-shrink-0', {
                      '-rotate-90': !isOpen,
                    })}
                  />
                  <h2 className="text-base font-semibold text-foreground">
                    {formatDomainLabel(domain)}
                  </h2>
                  <span className="text-xs text-muted">({patterns.length})</span>
                </button>
                {isOpen && (
                  <div className="mt-3 ml-2 grid gap-3 sm:grid-cols-2">
                    {patterns.map(p => (
                      <PatternCard key={p.pattern_id} pattern={p} />
                    ))}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
